import { useCallback, useMemo, useState } from 'react';
import { AuthOverlay } from '@khal-os/desktop-shell';

/**
 * Shown when the desktop has no enterprise config AND no build-time kernel
 * hint (`VITE_DEFAULT_KERNEL`). The user picks how they want to authenticate
 * via the 4-stage AuthOverlay (`choose | kernel | local | error`), then hands
 * off to platform's `/auth/kernel-sign-in` bridge with the entered URL.
 *
 * Replaces the prior silent fallback to a customer-specific hardcoded URL —
 * every workspace must be named explicitly.
 */
export function KernelUrlPromptScreen() {
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const platformUrl = useMemo(
		() => import.meta.env.VITE_PLATFORM_API_URL ?? 'https://platform.khal.ai',
		[],
	);

	const handleSubmitKernel = useCallback(
		async (url: string) => {
			const controller = new AbortController();
			const timeout = setTimeout(() => controller.abort(), 10_000);
			try {
				const res = await fetch(`${url.replace(/\/+$/, '')}/api/health`, {
					method: 'GET',
					signal: controller.signal,
				});
				if (!res.ok) {
					throw new Error(`Kernel responded with HTTP ${res.status}. Verify the address and try again.`);
				}
			} catch (err) {
				if (err instanceof DOMException && err.name === 'AbortError') {
					throw new Error('Kernel did not respond within 10 seconds. Verify the address and try again.');
				}
				if (err instanceof TypeError) {
					throw new Error('Could not reach that kernel. Check the URL and your network.');
				}
				throw err;
			} finally {
				clearTimeout(timeout);
			}

			const pwaOrigin = window.location.origin;
			const signInUrl = `${platformUrl}/auth/kernel-sign-in?kernel=${encodeURIComponent(url)}&pwaOrigin=${encodeURIComponent(pwaOrigin)}`;
			window.location.replace(signInUrl);
		},
		[platformUrl],
	);

	const handleSignIn = useCallback(() => {
		const defaultKernel = import.meta.env.VITE_DEFAULT_KERNEL;
		if (!defaultKernel) {
			// No known kernel — surface the manual-entry stage rather than
			// silently failing or hardcoding a customer URL.
			setErrorMessage(
				'No default kernel is configured for this build. Enter your workspace URL manually.',
			);
			return;
		}
		handleSubmitKernel(defaultKernel);
	}, [handleSubmitKernel]);

	const handleProceedLocal = useCallback(() => {
		// Local-kernel handoff is not yet wired end-to-end (pairing flow lives
		// behind a separate desktop initiative). Surface a clear notice rather
		// than dropping the user into a broken path.
		setErrorMessage(
			'Local kernels are not yet supported in this build. Connect to a remote kernel to continue.',
		);
	}, []);

	return (
		<AuthOverlay
			initialStage="choose"
			errorMessage={errorMessage ?? undefined}
			onSubmitKernel={handleSubmitKernel}
			onSignIn={handleSignIn}
			onProceedLocal={handleProceedLocal}
		/>
	);
}
